import type { ResponseResult } from '#shared/api';

import type { CCGeoLibrary } from '../model';

/** An empty library — what an unreadable or unrecognised payload becomes. */
export const EMPTY_GEO_LIBRARY: CCGeoLibrary = {
  serviceRegions: [],
  countries: [],
  subdivisions: [],
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Finds the geo library inside whatever the BE hands back.
 *
 * The endpoint does not exist yet, so its envelope is not settled, and the three
 * candidates already live in this codebase: NOP's bare payload, the
 * `{ data, dataGeneratedAt, errors, … }` envelope every other entity uses, and
 * the `{ status, cached, data, meta }` shape `/api/nop/*` answers with. All three
 * put the library at the top level or one `data` deep, so recognising it by its
 * own shape covers them without the FE having to guess which one lands.
 *
 * Anything unrecognised becomes an empty library rather than a throw: the map
 * degrades to no tints and static code labels, which is a usable map.
 */
export const unwrapGeoLibraryPayload = (raw: unknown): CCGeoLibrary => {
  if (!isObject(raw)) {
    return EMPTY_GEO_LIBRARY;
  }

  const candidate = Array.isArray(raw.serviceRegions) ? raw : raw.data;

  if (!isObject(candidate) || !Array.isArray(candidate.serviceRegions)) {
    return EMPTY_GEO_LIBRARY;
  }

  return {
    serviceRegions: candidate.serviceRegions,
    countries: Array.isArray(candidate.countries) ? candidate.countries : [],
    subdivisions: Array.isArray(candidate.subdivisions)
      ? candidate.subdivisions
      : [],
  } as CCGeoLibrary;
};

/** When the payload was generated, if the envelope carried it. */
const readGeneratedAt = (raw: unknown): string | null => {
  if (!isObject(raw)) {
    return null;
  }

  if (typeof raw.dataGeneratedAt === 'string') {
    return raw.dataGeneratedAt;
  }

  const meta = raw.meta;

  return isObject(meta) && typeof meta.generatedAt === 'string'
    ? meta.generatedAt
    : null;
};

/** Adapts a raw payload onto the response envelope the validator expects. */
export const asGeoLibraryResult = (
  raw: unknown,
): ResponseResult<CCGeoLibrary> => ({
  data: unwrapGeoLibraryPayload(raw),
  errors: null,
  expires: null,
  dataGeneratedAt: readGeneratedAt(raw),
  errorsGeneratedAt: null,
});
